import React, { useEffect, useState, useRef, useCallback } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './MapComponent.css';
import Sidebar from './components/Sidebar';
import Map from './components/Map';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  shadowSize: [41, 41]
});

const MapComponent = () => {
  const [hotspots, setHotspots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState({
    castle: true,
    church: true,
    house: true,
    building: true,
    ruin: true
  });
  const [allSelected, setAllSelected] = useState(true);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState({});
  const [focusedHotspot, setFocusedHotspot] = useState(null);
  const [visibleHotspots, setVisibleHotspots] = useState([]);
  const [mobileView, setMobileView] = useState(window.innerWidth <= 768 ? 'list' : 'map');
  const mapRef = useRef(null);
  const updateTimeoutRef = useRef(null);

  const updateVisibleHotspots = useCallback(() => {
    if (!hotspots.length) return;

    try {
      // On mobile, show all filtered hotspots in list view
      if (window.innerWidth <= 768) {
        const filtered = hotspots.filter(hotspot => selectedCategories[hotspot.category]);
        const sortedFiltered = [...filtered].sort((a, b) => {
          if (a.visibility === b.visibility) return 0;
          return a.visibility === 'public' ? -1 : 1;
        });
        setVisibleHotspots(sortedFiltered);
        return;
      }

      // On desktop, show hotspots in current map bounds
      if (mapRef.current) {
        const bounds = mapRef.current.getBounds();
        if (!bounds) return;

        const visible = hotspots.filter(hotspot => {
          const [lat, lng] = hotspot.location.split(',').map(Number);
          return bounds.contains([lat, lng]) && selectedCategories[hotspot.category];
        });

        const sortedVisible = [...visible].sort((a, b) => {
          if (a.visibility === b.visibility) return 0;
          return a.visibility === 'public' ? -1 : 1;
        });

        setVisibleHotspots(prev => {
          if (JSON.stringify(prev) !== JSON.stringify(sortedVisible)) {
            return sortedVisible;
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error updating visible hotspots:', error);
    }
  }, [hotspots, selectedCategories]);

  useEffect(() => {
    fetch('/hotspots.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to load hotspots data');
        }
        return response.json();
      })
      .then(data => {
        setHotspots(data);
        data.forEach(hotspot => {
          if (hotspot.image) {
            preloadImage(hotspot);
          }
        });
      })
      .catch(error => {
        console.error('Error loading hotspots:', error);
        setError(error.message);
      })
      .finally(() => setLoading(false));
  }, []);

  const preloadImage = useCallback((hotspot) => {
    const img = new Image();
    img.onload = () => {
      setPreloadedImages(prev => ({
        ...prev,
        [hotspot.name]: `/images/${hotspot.image}`
      }));
    };
    
    img.onerror = () => {
      const nonLocalizedImage = hotspot.image.replace('_l.jpg', '.jpg');
      const fallbackImg = new Image();
      
      fallbackImg.onload = () => {
        setPreloadedImages(prev => ({
          ...prev,
          [hotspot.name]: `/images/${nonLocalizedImage}`
        }));
      };
      
      fallbackImg.src = `/images/${nonLocalizedImage}`;
    };
    
    img.src = `/images/${hotspot.image}`;
  }, []);

  const handleLinkClick = useCallback((url) => {
    window.top.location.href = url;
  }, []);

  const handleMarkerClick = useCallback((hotspot) => {
    setFocusedHotspot(hotspot.name);
    // Remove scrolling to card on mobile
    if (window.innerWidth > 768) {
      const card = document.querySelector(`[data-hotspot="${hotspot.name}"]`);
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, []);

  const handleCardClick = useCallback((hotspot) => {
    setFocusedHotspot(hotspot.name);
    const [lat, lng] = hotspot.location.split(',').map(Number);
    if (mapRef.current) {
      mapRef.current.setView([lat, lng], 12);
      setMobileView('map');
    }
  }, []);

  useEffect(() => {
    updateVisibleHotspots();
  }, [selectedCategories, updateVisibleHotspots]);

  useEffect(() => {
    const handleResize = () => {
      updateVisibleHotspots();
      setMobileView(window.innerWidth <= 768 ? 'list' : 'map');
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [updateVisibleHotspots]);

  if (loading) {
    return <div className="loading">Loading map data...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  const filteredHotspots = hotspots.filter(hotspot => selectedCategories[hotspot.category]);

  return (
    <div className="app-container">
      <Sidebar 
        visibleHotspots={visibleHotspots}
        preloadedImages={preloadedImages}
        focusedHotspot={focusedHotspot}
        handleCardClick={handleCardClick}
        handleLinkClick={handleLinkClick}
        selectedCategories={selectedCategories}
        setSelectedCategories={setSelectedCategories}
        allSelected={allSelected}
        setAllSelected={setAllSelected}
        filtersVisible={filtersVisible}
        setFiltersVisible={setFiltersVisible}
        mobileView={mobileView}
        setMobileView={setMobileView}
      />
      <Map 
        filteredHotspots={filteredHotspots}
        preloadedImages={preloadedImages}
        handleMarkerClick={handleMarkerClick}
        handleLinkClick={handleLinkClick}
        mapRef={mapRef}
        updateVisibleHotspots={updateVisibleHotspots}
        setFiltersVisible={setFiltersVisible}
        setFocusedHotspot={setFocusedHotspot}
        updateTimeoutRef={updateTimeoutRef}
        mobileView={mobileView}
        setMobileView={setMobileView}
      />
    </div>
  );
};

export default MapComponent;